import { useSharedState, useUpdateSharedState } from '@/hooks/state/shared-state';
import { navigateTo } from '@/utils/navigation';
import { hasValidTheaterLocation } from '@/utils/theaters/theater-url';
import { PagedListOfTheater } from '@cinemark/content-api-client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useUpdateTheaterContext } from './state/theater';
import { useUpdateTheatersContext } from './state/theaters';
import { useTheatersByZipCode } from './theater/theaters';

export type UseZipCodeTheaterSearchByType = 'search-and-redirect' | 'return-results' | 'compact-search-and-redirect' | 'set-location';

const nearbyTheatersUrl = '/theatres?tab=nearby';

type SearchByZipResponseType = {
  data: PagedListOfTheater;
  error?: string;
};

type UseZipCodeTheaterSearchType = {
  searchByZip: (zipCode: string, type?: UseZipCodeTheaterSearchByType) => Promise<SearchByZipResponseType>;
};

export const useZipCodeTheaterSearch = (): UseZipCodeTheaterSearchType => {
  const [zipCodeWithType, setZipCodeWithType] = useState<{ type?: UseZipCodeTheaterSearchByType; zipCode: string; }>({ zipCode: '' });
  const pendingPromise = useRef<{ resolve: (value: SearchByZipResponseType) => void; } | null>(null);

  const { data: sharedState } = useSharedState();
  const { mutateAsync: updateSharedState } = useUpdateSharedState();
  const { data: theaters, isError, isLoading, isSuccess } = useTheatersByZipCode(zipCodeWithType.zipCode);
  const { mutateAsync: updateTheaterContext } = useUpdateTheaterContext();
  const { mutateAsync: updateTheatersContext } = useUpdateTheatersContext();

  useEffect(() => {
    const updateTheaterData = async (): Promise<void> => {
      if (!pendingPromise.current) return;

      const baseResults: PagedListOfTheater = {
        data: [],
        hasNextPage: false,
        hasPreviousPage: false,
        pageCount: 0,
        pageNumber: 0,
        pageSize: 0,
        totalItemCount: 0,
      };

      if (isError) {
        pendingPromise.current.resolve({
          data: baseResults,
          error: 'We were unable to search and find the theatre closest to you by your zip code.',
        });
        pendingPromise.current = null;

        return;
      }

      const isRedirectSearch = zipCodeWithType.type === 'search-and-redirect' || zipCodeWithType.type === 'compact-search-and-redirect';
      const shouldSetData = isRedirectSearch || zipCodeWithType.type === 'set-location';

      if (theaters?.data?.length && shouldSetData) {
        const firstTheater = theaters.data[0];
        if (!hasValidTheaterLocation(firstTheater)) {
          pendingPromise.current.resolve({
            data: baseResults,
            error: 'Invalid ZIP Code. Please try again.',
          });
          pendingPromise.current = null;

          return;
        }

        updateTheaterContext(firstTheater);
        updateTheatersContext(theaters);

        await updateSharedState({
          lastTextSearch: zipCodeWithType.zipCode ? parseInt(zipCodeWithType.zipCode) : undefined,
          lastViewedTheaterId: firstTheater.theaterId,
          lastViewedTheaterUrl: isRedirectSearch ? nearbyTheatersUrl : sharedState?.lastViewedTheaterUrl,
        });

        if (isRedirectSearch) {
          navigateTo(nearbyTheatersUrl);
        }
      }

      await updateSharedState({
        lastTextSearch: zipCodeWithType.zipCode ? parseInt(zipCodeWithType.zipCode) : undefined,
      });

      pendingPromise.current.resolve({ data: theaters || baseResults });
      pendingPromise.current = null;
    };

    if (!isLoading && (isSuccess || isError)) {
      updateTheaterData();
    }
  }, [isError, isLoading, isSuccess, pendingPromise, sharedState?.lastViewedTheaterUrl,
    theaters, updateSharedState, updateTheaterContext, updateTheatersContext, zipCodeWithType]);

  const searchByZip = useCallback((zipCodeInput: string, type?: UseZipCodeTheaterSearchByType): Promise<SearchByZipResponseType> => {
    setZipCodeWithType({ type, zipCode: zipCodeInput });
    return new Promise(resolve => pendingPromise.current = { resolve });
  }, []);

  return { searchByZip };
};
